import { createTenantSchema } from "@closer/shared";
import { Button, FormField, Input, Modal, Select, useToast } from "@closer/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import type { z } from "zod";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { applyServerErrors, useZodForm } from "../../lib/useZodForm";

type CreateTenantValues = z.infer<typeof createTenantSchema>;

export interface CreateTenantModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateTenantModal({ open, onClose }: CreateTenantModalProps) {
  const { setActiveTenant } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  /**
   * Tür formda SEÇİLİYOR (CLOS-71 `[023]`).
   *
   * Tür DEĞİŞTİRİLEMEZ (DB'de kolon yazma yetkisi geri alınmış), bu yüzden kullanıcı ne
   * seçtiğini GÖREREK karar vermeli. Gizli bir alan olarak göndermek, geri alınamayacak
   * bir seçimi kullanıcıdan saklamak olurdu.
   *
   * Varsayılan `enterprise_company`: iki seçenekten biri işaretli gelmeli, yoksa form
   * "seçmedim" ile "şirket seçtim" arasında ayrım yapamaz. Kullanıcı hukuk bürosunu
   * açıkça seçiyor.
   */
  const form = useZodForm(createTenantSchema, {
    defaultValues: { name: "", type: "enterprise_company" } as CreateTenantValues,
  });

  const mutation = useMutation({
    mutationFn: async (values: CreateTenantValues) =>
      (
        await api<{ id: string; name: string }>("/api/tenants", {
          method: "POST",
          body: JSON.stringify(values),
        })
      ).data,
    onSuccess: async (tenant) => {
      await queryClient.invalidateQueries({ queryKey: ["memberships"] });
      toast.success("Firma oluşturuldu");
      form.reset();
      onClose();
      setActiveTenant(tenant.id);
      navigate("/app");
    },
    onError: (error) => {
      applyServerErrors(form, error);
      toast.error(error instanceof ApiRequestError ? error.message : "Firma oluşturulamadı");
    },
  });

  const onSubmit = form.handleSubmit((values) => mutation.mutate(values));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Yeni Firma"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Vazgeç
          </Button>
          <Button type="submit" form="create-tenant-form" loading={mutation.isPending}>
            Oluştur
          </Button>
        </>
      }
    >
      <form id="create-tenant-form" onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <FormField label="Firma adı" error={form.formState.errors.name?.message} required>
          <Input autoComplete="organization" {...form.register("name")} />
        </FormField>

        <FormField label="Organizasyon türü" error={form.formState.errors.type?.message} required>
          <Select {...form.register("type")}>
            <option value="enterprise_company">Şirket</option>
            <option value="enterprise_law_firm">Hukuk Bürosu</option>
          </Select>
        </FormField>
      </form>
    </Modal>
  );
}
